import { memo } from "react";
import { ProductClass, pci as toPCI } from "@atlas-int/data/types";
import type { RCA, PCI, COG, Distance } from "@atlas-int/data/types";
import { useOptionalTooltipDispatchRef } from "../../hooks/TooltipStateIsolationProvider";
import type { TooltipProductDatum } from "../../types/atlasTooltip";

interface ScatterplotDataPoint {
  productId?: string | null;
  cx: number;
  cy: number;
  r: number;
  opacity: number;
  fill: string;
  fillOpacity: number;
  distance?: Distance | null;
  exportRca?: RCA | null;
  cog?: COG | null;
  countryId?: string | null;
}

interface MetaDataNodeData {
  productId: string;
  nameShortEn: string;
  code?: string;
  topParent?: { productId: string };
  [key: string]: unknown;
}

interface AnimatedPointsProps {
  filteredData: ScatterplotDataPoint[];
  ySelection: string;
  metaDataLookup: Map<string, { data: MetaDataNodeData }>;
  highlightedItem: string | null | undefined;
  setHighlightedItem: (id: string | null) => void;
  productClass: ProductClass;
  totalExportLookup: Map<string, { exportValue?: number; pci?: number }>;
  setHoveredItem?: (id: string | null) => void;
}

const AnimatedPoints: React.FC<AnimatedPointsProps> = ({
  filteredData,
  ySelection,
  metaDataLookup,
  highlightedItem,
  setHighlightedItem,
  productClass,
  totalExportLookup,
  setHoveredItem,
}) => {
  const dispatchRef = useOptionalTooltipDispatchRef();

  return (
    <>
      {filteredData.map((item) => {
        const productData = metaDataLookup.get(item.productId)?.data;
        const sectorName = metaDataLookup.get(
          productData?.topParent?.productId,
        )?.data?.nameShortEn;

        return (
          <circle
            key={`${item.productId}-${ySelection}`}
            cx={item.cx}
            cy={item.cy}
            r={item.r}
            opacity={item.opacity}
            fill={item.fill}
            fillOpacity={item.fillOpacity}
            id={productData?.nameShortEn}
            stroke="black"
            cursor="pointer"
            style={{
              transition:
                "cx 400ms ease, cy 400ms ease, r 400ms ease, opacity 400ms ease, fill 400ms ease, fill-opacity 400ms ease",
            }}
            onClick={(e) => {
              setHighlightedItem(item.productId);
              if (dispatchRef) {
                dispatchRef.current.fixTooltip(
                  {
                    kind: "product",
                    nameEn: productData?.nameShortEn,
                    productCode: productData?.code,
                    productId: item.productId ?? undefined,
                    topLevelParent: productData?.topParent?.productId,
                    exportRca: item.exportRca ?? undefined,
                    distance: item.distance ?? undefined,
                    pci: totalExportLookup.get(item.productId)?.pci != null ? toPCI(totalExportLookup.get(item.productId)!.pci!) : undefined,
                    exportValue: totalExportLookup.get(item.productId)?.exportValue,
                  } as unknown as TooltipProductDatum,
                  [e.clientX, e.clientY],
                  item.fill,
                );
              }
            }}
            onMouseEnter={() => {
              setHoveredItem?.(item.productId);
              if (dispatchRef && highlightedItem !== item.productId) {
                dispatchRef.current.showTooltip(
                  {
                    kind: "product",
                    nameEn: productData?.nameShortEn,
                    productCode: productData?.code,
                    productId: item.productId ?? undefined,
                    topLevelParent: productData?.topParent?.productId,
                    exportRca: item.exportRca ?? undefined,
                    distance: item.distance ?? undefined,
                    pci: totalExportLookup.get(item.productId)?.pci != null ? toPCI(totalExportLookup.get(item.productId)!.pci!) : undefined,
                    exportValue: totalExportLookup.get(item.productId)?.exportValue,
                  } as unknown as TooltipProductDatum,
                  undefined,
                  item.fill,
                );
              }
            }}
            onMouseLeave={() => {
              setHoveredItem?.(null);
              if (dispatchRef && highlightedItem !== item.productId) {
                dispatchRef.current.hideTooltip();
              }
            }}
          />
        );
      })}
    </>
  );
};

const MemoizedAnimatedPoints = memo(AnimatedPoints);
export default MemoizedAnimatedPoints;
